import { useQCockpitInfra } from './useQCockpitInfra';
import { useBrokerLoginMonitor } from './useBrokerLoginMonitor';
import {
    formatChipText,
    formatLatency,
    formatNumber,
    formatTimestamp,
    toneForStatus,
} from './components/SharedFrames';

export function usePlatformStateModel() {
    const { infra, loading: infraLoading, error: infraError } = useQCockpitInfra();
    const {
        digest: brokerLoginDigest,
        loading: brokerLoginLoading,
        error: brokerLoginError,
        lastRunAgeMs: brokerLoginAgeMs,
    } = useBrokerLoginMonitor();

    const mdNorm = infra?.mdNorm || null;
    const quantPlatform = infra?.quantPlatform || null;
    const mdNormSnapshot = mdNorm?.snapshot || null;
    const quantSnapshot = quantPlatform?.snapshot || null;
    const chainRows = quantSnapshot?.chains || [];
    const infraIssues = (infra?.services || []).filter((service) => service.status !== 'operational');
    const serviceCount = (infra?.services || []).length;
    const operationalCount = (infra?.services || []).filter((service) => service.status === 'operational').length;
    const platformStatusTone = infraError
        ? 'error'
        : infraLoading
            ? 'warning'
            : toneForStatus(infra?.overallStatus);

    const platformOverviewMetrics = [
        {
            label: 'Infra Status',
            value: formatChipText(infra?.overallStatus || (infraLoading ? 'loading' : 'waiting')),
            tone: platformStatusTone,
        },
        {
            label: 'Operational Services',
            value: serviceCount ? `${operationalCount}/${serviceCount}` : '—',
            tone: serviceCount && operationalCount === serviceCount ? 'success' : 'warning',
        },
        {
            label: 'Active Chains',
            value: formatNumber(quantSnapshot?.activeChainCount ?? chainRows.length, 0),
        },
        {
            label: 'Active Tokens',
            value: formatNumber(quantSnapshot?.activeTokenCount, 0),
        },
        {
            label: 'Market-Data Age',
            value: mdNorm?.ageMs == null ? '—' : formatLatency(mdNorm.ageMs, 0),
        },
        {
            label: 'Runtime Tick Age',
            value: quantSnapshot?.lastTickAgeS == null ? '—' : `${formatNumber(quantSnapshot.lastTickAgeS, 1)}s`,
        },
    ];

    const mdNormMetrics = [
        {
            label: 'WS',
            description: 'Whether md_norm currently has an authenticated live WebSocket session to Zerodha.',
            value: mdNormSnapshot?.wsConnected ? 'Connected' : 'Disconnected',
            tone: mdNormSnapshot?.wsConnected ? 'success' : 'error',
        },
        {
            label: 'Age',
            description: 'How old the md_norm telemetry snapshot itself is in qCockpit.',
            value: mdNorm?.ageMs == null ? '—' : formatLatency(mdNorm.ageMs, 0),
        },
        {
            label: 'Last Message',
            description: 'Time since the most recent WebSocket frame was seen by md_norm.',
            value: formatLatency(mdNormSnapshot?.lastWsMessageAgeMs, 0),
        },
        {
            label: 'Universe',
            description: 'Current number of instrument tokens md_norm believes are in the active subscription universe.',
            value: formatNumber(mdNormSnapshot?.universeSize, 0),
        },
        {
            label: 'Ticks Ingested',
            description: 'Running count of ticks accepted by md_norm since the process started.',
            value: formatNumber(mdNormSnapshot?.ticksIngested, 0),
        },
        {
            label: 'Drops',
            description: 'Running count of ticks or candles dropped under queue pressure or publish backpressure.',
            value: formatNumber(mdNormSnapshot?.ticksDropped, 0),
            tone: Number(mdNormSnapshot?.ticksDropped) > 0 ? 'warning' : 'default',
        },
        {
            label: 'Reconnects',
            description: 'How many times md_norm has had to reconnect its Zerodha WebSocket session.',
            value: formatNumber(mdNormSnapshot?.wsReconnects, 0),
        },
        {
            label: 'XADD',
            description: 'Latency of the latest Redis stream publish batch from md_norm into the prod streams.',
            value: formatLatency(mdNormSnapshot?.lastXaddLatencyMs),
        },
        {
            label: 'Parse to Publish',
            description: 'Approximate end-to-end hot-path latency from message parse to Redis publish inside md_norm.',
            value: formatLatency(mdNormSnapshot?.lastParseToPublishMs),
        },
        {
            label: 'Redis Failures',
            description: 'Running count of Redis publish or connect failures seen by md_norm.',
            value: formatNumber(mdNormSnapshot?.redisFailures, 0),
            tone: Number(mdNormSnapshot?.redisFailures) > 0 ? 'warning' : 'default',
        },
    ];

    const quantMetrics = [
        {
            label: 'Consumer',
            description: 'Whether the Python Redis stream consumer loop inside quant-platform is running.',
            value: quantSnapshot?.consumerRunning ? 'Running' : 'Stopped',
            tone: quantSnapshot?.consumerRunning ? 'success' : 'error',
        },
        {
            label: 'Kill Switch',
            description: 'Current platform safety latch state. ACTIVE means the runtime is armed and processing.',
            value: quantSnapshot?.killSwitchState || '—',
            tone: toneForStatus(quantSnapshot?.killSwitchState),
        },
        {
            label: 'Age',
            description: 'How old the quant-platform telemetry snapshot itself is in qCockpit.',
            value: quantPlatform?.ageMs == null ? '—' : formatLatency(quantPlatform.ageMs, 0),
        },
        {
            label: 'Last Tick',
            description: 'Seconds since quant-platform last consumed a tick from the Redis stream.',
            value: quantSnapshot?.lastTickAgeS == null ? '—' : `${formatNumber(quantSnapshot.lastTickAgeS, 1)}s`,
        },
        {
            label: 'Active Tokens',
            description: 'Current number of tokens in the Python-published live universe.',
            value: formatNumber(quantSnapshot?.activeTokenCount, 0),
        },
        {
            label: 'Active Chains',
            description: 'How many option-chain subscriptions quant-platform currently has registered.',
            value: formatNumber(quantSnapshot?.activeChainCount, 0),
        },
        {
            label: 'Base-only',
            description: 'Count of chains still subscribed only to spot or future base tokens because a usable live spot was not available yet.',
            value: formatNumber(quantSnapshot?.baseOnlyChainCount, 0),
        },
        {
            label: 'Decode Errors',
            description: 'Running count of Redis stream messages that quant-platform failed to decode or process cleanly.',
            value: formatNumber(quantSnapshot?.decodeErrors, 0),
            tone: Number(quantSnapshot?.decodeErrors) > 0 ? 'warning' : 'default',
        },
        {
            label: 'Ticks',
            description: 'Running count of ticks processed by quant-platform since the current process started.',
            value: formatNumber(quantSnapshot?.ticksProcessed, 0),
        },
        {
            label: 'Snapshot Age',
            description: 'Age of the stalest currently tracked option-chain snapshot across the active chains.',
            value: quantSnapshot?.maxSnapshotAgeS == null ? '—' : `${formatNumber(quantSnapshot.maxSnapshotAgeS, 1)}s`,
        },
    ];

    const brokerLoginOkCount = brokerLoginDigest?.okCount ?? 0;
    const brokerLoginTotal = brokerLoginDigest?.totalCount ?? 0;
    const brokerLoginFailing = brokerLoginDigest?.failingAccounts ?? [];
    const brokerLoginLastRunAgeHours =
        brokerLoginAgeMs == null ? null : brokerLoginAgeMs / 3_600_000;
    const brokerLoginStatusLabel = brokerLoginError
        ? 'error'
        : brokerLoginLoading && !brokerLoginDigest
            ? 'loading'
            : brokerLoginDigest?.overallStatus || 'waiting';
    const brokerLoginMetrics = [
        {
            label: 'Status',
            description: 'Overall health of the daily broker_login run across all enabled accounts.',
            value: formatChipText(brokerLoginStatusLabel),
            tone: toneForStatus(brokerLoginDigest?.overallStatus),
        },
        {
            label: 'Accounts OK',
            description: 'Accounts whose most recent login attempt succeeded.',
            value: brokerLoginTotal ? `${brokerLoginOkCount}/${brokerLoginTotal}` : '—',
            tone:
                brokerLoginTotal === 0
                    ? 'default'
                    : brokerLoginOkCount === brokerLoginTotal
                        ? 'success'
                        : brokerLoginFailing.length
                            ? 'warning'
                            : 'default',
        },
        {
            label: 'Last Run',
            description: 'Timestamp of the most recent broker_login activity snapshot or event.',
            value: formatTimestamp(brokerLoginDigest?.lastRunAt),
        },
        {
            label: 'Run Age',
            description: 'Hours since the most recent broker_login run. The systemd timer fires daily at 07:55 IST.',
            value:
                brokerLoginLastRunAgeHours == null
                    ? '—'
                    : `${formatNumber(brokerLoginLastRunAgeHours, 1)}h`,
            tone:
                brokerLoginLastRunAgeHours == null
                    ? 'default'
                    : brokerLoginLastRunAgeHours > 26
                        ? 'warning'
                        : 'default',
        },
        {
            label: 'Failing',
            description: 'Accounts whose last login attempt errored out.',
            value: brokerLoginFailing.length
                ? brokerLoginFailing.join(', ')
                : 'None',
            tone: brokerLoginFailing.length ? 'error' : 'success',
        },
        {
            label: 'Last Message',
            description: 'Most recent broker_login event message (success or failure reason).',
            value: brokerLoginDigest?.lastRunMessage || '—',
        },
    ];

    const platformBanner = infraError
        ? {
              tone: 'error',
              label: 'Platform Telemetry',
              message: `qCockpit could not load infra telemetry. ${infraError.message || 'Please check the Insights backend route.'}`,
          }
        : infraLoading
            ? {
                  tone: 'info',
                  label: 'Platform Telemetry',
                  message: 'Loading md_norm and quant-platform telemetry into the platform lane.',
              }
            : infraIssues.length
                ? {
                      tone: 'warning',
                      label: 'Platform Telemetry',
                      message: `Attention required: ${infraIssues.map((service) => `${service.key.split(':')[1]}=${service.status}`).join(' · ')}.`,
                  }
                : {
                      tone: 'success',
                      label: 'Platform Telemetry',
                      message: 'Infrastructure telemetry is healthy across md_norm and quant-platform.',
                  };

    return {
        infra,
        infraLoading,
        infraError,
        mdNorm,
        quantPlatform,
        mdNormSnapshot,
        quantSnapshot,
        chainRows,
        infraIssues,
        serviceCount,
        operationalCount,
        platformStatusTone,
        brokerLoginDigest,
        brokerLoginLoading,
        brokerLoginError,
        brokerLoginAgeMs,
        brokerLoginStatusLabel,
        platformOverviewMetrics,
        mdNormMetrics,
        quantMetrics,
        brokerLoginMetrics,
        platformBanner,
    };
}
